import React, { useEffect, useState } from 'react';
import { Loader } from 'lucide-react';
import { setAuthToken } from '../utils/auth';
import { authAPI } from '../services/api';

const PRIVACY_POLICY_VERSION = '1.0';

const trackSignupConversion = () => {
  if (typeof window === 'undefined') return;

  const conversionId = `signup-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'sign_up',
    method: 'google',
    conversionId
  });

  if (typeof window.rdt === 'function') {
    window.rdt('track', 'SignUp', {
      conversionId
    });
  }
};

const GoogleAuthCallback = () => {
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Completing authentication...');
  const [showConsent, setShowConsent] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [marketingChecked, setMarketingChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [consentError, setConsentError] = useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const error = urlParams.get('error');
    const needsConsent = urlParams.get('needsConsent') === 'true';

    if (error) {
      setStatus('error');
      setMessage(decodeURIComponent(error));
      setTimeout(() => { window.location.href = '/'; }, 3000);
      return;
    }

    if (token) {
      setAuthToken(token, true);
      if (needsConsent) {
        setStatus('consent');
        setShowConsent(true);
      } else {
        setStatus('success');
        setMessage('Welcome back! Redirecting...');
        setTimeout(() => { window.location.href = '/'; }, 1200);
      }
    } else {
      setStatus('error');
      setMessage('No authentication token received');
      setTimeout(() => { window.location.href = '/'; }, 3000);
    }
  }, []);

  const handleConsentSubmit = async () => {
    if (!privacyChecked) {
      setConsentError('Please accept the Privacy Policy to continue.');
      return;
    }

    setConsentError('');
    setSubmitting(true);
    try {
      await authAPI.saveMarketingConsent(marketingChecked, privacyChecked, PRIVACY_POLICY_VERSION);
      trackSignupConversion();
      window.location.href = '/';
    } catch (_) {
      setSubmitting(false);
      setConsentError('We could not save your consent. Please try again.');
    }
  };

  const container = {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', minHeight: '100vh',
    backgroundColor: '#0a0a0f', color: '#F5F5F5'
  };

  if (status === 'processing') return (
    <div style={container}>
      <Loader size={48} style={{ color: '#8B5CF6', marginBottom: '20px', animation: 'spin 1s linear infinite' }} />
      <p style={{ color: '#A3A3A3', fontSize: '14px' }}>{message}</p>
    </div>
  );

  if (status === 'error') return (
    <div style={container}>
      <div style={{ fontSize: '48px', marginBottom: '16px', color: '#EF4444' }}>✗</div>
      <p style={{ color: '#EF4444', fontSize: '14px' }}>{message}</p>
    </div>
  );

  if (status === 'success') return (
    <div style={container}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>✓</div>
      <p style={{ color: '#A3A3A3', fontSize: '14px' }}>{message}</p>
    </div>
  );

  if (showConsent) return (
    <div style={container}>
      <div style={{
        backgroundColor: '#111118', border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '16px', padding: '40px', maxWidth: '480px', width: '90%'
      }}>
        <img src="/assets/brand/logo-nobg.png" alt="ReactorHub" style={{ height: '56px', marginBottom: '20px' }} />

        <h2 style={{ fontSize: '22px', fontWeight: '800', marginBottom: '8px', color: '#fff' }}>
          Welcome to ReactorHub
        </h2>
        <p style={{ fontSize: '14px', color: '#A3A3A3', marginBottom: '28px', lineHeight: '1.6' }}>
          Your account is ready. Please review the required privacy consent before continuing.
        </p>

        <div style={{
          backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '10px', padding: '20px', marginBottom: '16px'
        }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={privacyChecked}
              onChange={e => {
                setPrivacyChecked(e.target.checked);
                if (e.target.checked) setConsentError('');
              }}
              style={{ marginTop: '3px', width: '16px', height: '16px', cursor: 'pointer', accentColor: '#8B5CF6' }}
            />
            <div>
              <p style={{ fontSize: '14px', fontWeight: '700', color: '#F5F5F5', marginBottom: '6px' }}>
                I agree to the Privacy Policy
              </p>
              <p style={{ fontSize: '13px', color: '#A3A3A3', lineHeight: '1.6' }}>
                I understand ReactorHub uses platform interaction data, such as clicks, favorites, votes, and forum activity, to operate analytics and creator tools.
              </p>
            </div>
          </label>
        </div>

        <div style={{
          backgroundColor: 'rgba(139, 92, 246, 0.08)', border: '1px solid rgba(139, 92, 246, 0.2)',
          borderRadius: '10px', padding: '20px', marginBottom: '24px'
        }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={marketingChecked}
              onChange={e => setMarketingChecked(e.target.checked)}
              style={{ marginTop: '3px', width: '16px', height: '16px', cursor: 'pointer', accentColor: '#8B5CF6' }}
            />
            <div>
              <p style={{ fontSize: '14px', fontWeight: '600', color: '#F5F5F5', marginBottom: '6px' }}>
                Keep me updated with ReactorHub content
              </p>
              <p style={{ fontSize: '13px', color: '#A3A3A3', lineHeight: '1.6' }}>
                Get notified about the best reaction videos, trending reactors, community highlights, platform updates, and exclusive content picks — delivered straight to your inbox.
              </p>
            </div>
          </label>
        </div>

        <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '24px', lineHeight: '1.5' }}>
          This is optional and unchecked by default. You can unsubscribe at any time from your account settings. We will never share your email with third parties.
        </p>

        {consentError && (
          <p style={{ fontSize: '13px', color: '#F87171', marginBottom: '16px', lineHeight: '1.5' }}>
            {consentError}
          </p>
        )}

        <button
          onClick={handleConsentSubmit}
          disabled={submitting || !privacyChecked}
          style={{
            width: '100%', padding: '14px',
            background: 'linear-gradient(90deg, #8B5CF6, #F97316)',
            border: 'none', borderRadius: '8px', color: '#fff',
            fontSize: '15px', fontWeight: '700', cursor: submitting || !privacyChecked ? 'not-allowed' : 'pointer',
            opacity: submitting || !privacyChecked ? 0.55 : 1
          }}
        >
          {submitting ? 'Saving...' : 'Continue to ReactorHub'}
        </button>
      </div>
    </div>
  );

  return null;
};

export default GoogleAuthCallback;
